import { compile } from 'path-to-regexp';

/**
 * the purpose of this is to be able to do things like:
 * <Link to={getRoute(RouteDefinitions.customer.Edit, { id : 'id' })} />Edit </Link>
 */

type RouteParams = { [property: string]: string | number | undefined };

export const RouteDefinitions = {
  root: '/',
  login: '/login',
  dashboard: '/dashboard',
  onlineToDo: '/online-to-do',
  help: {
    Title: 'Help',
    Details: '/help'
  },
  authorization: {
    Title: 'Authorization',
    Create: '/authorization',
    Edit: '/authorization/:swoId',
    Search: '/authorization/search',
    Details: '/authorization/:swoId/details',
    ManageInventory: '/authorization/:swoId/manage-inventory'
  },
  authorizationVersion: {
    Title: 'Authorization',
    Details: '/authorization/:swoId/details/:swoDetail?/version/:version?'
  },
  escrowofficer: {
    Title: 'Escrow Officer',
    Create: '/escrow-officer',
    Edit: '/escrow-officer/:id?',
    Search: '/escrow-officers',
    Details: '/escrow-officer/:id/details'
  },
  escrowoffice: {
    Title: 'Escrow Office',
    Create: '/escrow-office',
    Edit: '/escrow-office/:id?',
    Search: '/escrow-offices',
    Details: '/escrow-office/:id/details'
  },
  customer: {
    Title: 'Customer',
    Create: '/customer',
    Edit: '/customer/:id?',
    Search: '/customers',
    Details: '/customer/:id/details'
  },
  invoicing: {
    Title: 'Invoicing',
    Create: '/swo/:swoId/invoicing',
    Edit: '/invoicing/:invoiceId',
    Search: '/invoices',
    Details: '/invoicing/:invoiceId/details'
  },
  profile: {
    Title: 'Profile',
    Create: '/profile',
    Edit: '/profile/:id?'
  },
  realestateagent: {
    Title: 'Real Estate Agent',
    Create: '/realestate-agent',
    Edit: '/realestate-agent/:id?',
    Search: '/realestate-agents',
    Details: '/realestate-agent/:id/details'
  },
  realestateassistant: {
    Title: 'Real Estate Assistant',
    Create: '/realestate-assistant',
    Edit: '/realestate-assistant/:id?'
  },
  realestateoffice: {
    Title: 'Real Estate Office',
    Create: '/realestate-office',
    Edit: '/realestate-office/:id?',
    Search: '/realestate-offices',
    Details: '/realestate-office/:id/details'
  },
  transactioncoordinator: {
    Title: 'Transaction Coordinator',
    Create: '/transaction-coordinator',
    Edit: '/transaction-coordinator/:id?',
    Search: '/transaction-coordinators',
    Details: '/transaction-coordinator/:id/details'
  },
  user: {
    Title: 'User',
    Create: '/user',
    Edit: '/user/:id?',
    List: '/users'
  },
  contract: {
    Title: 'Contract',
    Create: '/contract',
    OrderEdit: '/contract/:id',
    Search: '/contracts',
    Details: '/contract/:id/details/',
    Cancellation: '/contract/:id/cancellation',
    Transfer: '/contract/:id/transfer',
    NoRenew: '/contract/:id/norenew',
    Emails: '/contract/:id/emails',
    PaymentEdit: '/contract/:id/payment',
    Swo: '/contract/:id/swo',
    Balances: '/contract/:id/balances',
    RenewalOptions: '/contract/:id/renewal-options'
  },
  contract2: {
    Title: 'Contract - Hosted Checkout',
    Create: '/contract2',
    OrderEdit: '/contract/:id',
    Details: '/contract/:id/details/',
    Cancellation: '/contract/:id/cancellation',
    Transfer: '/contract/:id/transfer',
    NoRenew: '/contract/:id/norenew'
  },
  version: '/version',
  cil: {
    Title: 'CIL Offer',
    Search: '/cil-offers'
  },
  cilNotification: {
    Title: ' ',
    Notification: '/cilnotification'
  },
  autoautho: '/autoautho',
  quote: {
    Title: 'Quote',
    Create: '/quote',
    Search: '/quotes',
    Details: '/quote/:id/details'
  },
  propertymanagementoffice: {
    Title: 'Property Management Office',
    Create: '/property-management-office',
    Edit: '/property-management-office/:id?',
    Search: '/property-management-offices',
    Details: '/property-management-office/:id/details'
  },
  propertymanager: {
    Title: 'Property Manager',
    Create: '/property-manager',
    Edit: '/property-manager/:id?',
    Search: '/property-managers',
    Details: '/property-manager/:id/details'
  },
  vendor: {
    Title: 'Vendor',
    Create: '/vendor',
    Edit: '/vendor/:id',
    Search: '/vendor-dashboard',
    Details: '/vendor/:id/details',
    TradeMaintenace: '/vendor-trade-maintenance/:id',
    // Temporary route paths for sake of creating nav
    PriceAgreements: '/vendor///price-agreements',
    VQAs: '/vendor///vqas',
    Rerating: '/vendor///rerating',
    Groups: '/vendor///groups'
  },
  tableofcontents: '/table-of-contents',
  renewal: {
    Title: 'Contract Renewal',
    NoChanges: '/renewal-no-changes',
    WithChanges: '/renewal-with-changes'
  },
  serviceworkorder: {
    Title: 'Service Work Order',
    Edit: '/service-work-order/:id?',
    Search: '/service-work-orders',
    Details: '/service-work-order/:id/details',
    Payment: '/service-work-order/:id/payment'
  },
  dispatch: {
    Title: 'Dispatch',
    Landing: '/dispatch'
  },
  zerovendor: {
    Title: 'Zero Vendor',
    Landing: '/zero-vendor'
  },
  purchasing: {
    Title: 'Purchasing',
    Search: '/purchasing',
    Details: '/purchasing/:id/details'
  },
  accounting: {
    Title: 'Accounting',
    List: '/accounting'
  },
  transaction: {
    Title: 'Payment',
    TransactionLanding: '/transaction-landing/:transactionId'
  },
  tutorial: {
    Title: 'Tutorials',
    AutoAuthoTutorial: '/autoauthotutorial',
    SWOStatusTutorial: '/swostatustutorial'
  },
  payeezyTest: {
    Title: 'Test Payeezy Gateway',
    launch: '/test',
    landing: '/testlanding'
  }
};

class RouteDefinitionManager {
  compiledRoutes: any;

  compileAllRoutes(keys: string[], obj: Object): { [property: string]: Function } {
    let result = {};
    keys.forEach(key => {
      const value = obj[key];
      if (typeof value === 'string') {
        result[key] = compile(value);
      } else {
        result[key] = this.compileAllRoutes(Object.keys(value), value);
      }
    });
    return result;
  }

  wasAlreadyCompiled(page: string): boolean {
    return this.compiledRoutes && this.compiledRoutes[page];
  }

  addCompiled(page: string, generator: Function) {
    this.compiledRoutes = {
      ...this.compiledRoutes,
      [page]: generator
    };
  }

  getLink(page: string, params?: RouteParams) {
    if (!params) {
      return page;
    }
    const compiled = this.wasAlreadyCompiled(page);
    let generator = undefined;
    if (!compiled) {
      generator = compile(page);
      this.addCompiled(page, generator);
    } else {
      generator = this.compiledRoutes[page];
    }
    const clean = this.cleanParams(params);

    return generator(clean);
  }

  getModuleName(location: string) {
    const currentUrl: string = location.replace(/-/gi, '').replace(/\//gi, '');

    for (let prop in RouteDefinitions) {
      if (prop) {
        const re = new RegExp(prop);
        const res = new RegExp(prop + 's');
        if (currentUrl.search(re) >= 0 || currentUrl.search(res) >= 0 || prop === currentUrl) {
          return RouteDefinitions[prop].Title || 'Dashboard';
        }
      }
    }
  }

  private cleanParams(params: RouteParams): { [property: string]: string } {
    let newParams = {};
    const keys = Object.keys(params);
    keys.forEach(key => {
      if (!params[key] && typeof params[key] === 'undefined') {
        newParams[key] = 'undefined';
        return;
      }

      if (typeof params[key] !== 'string') {
        newParams[key] = '' + params[key];
      }
    });

    return newParams;
  }
}

const routeManager = new RouteDefinitionManager();

export function getRoute(page: string, params?: RouteParams) {
  return routeManager.getLink(page, params);
}

export function getModule(location: string): string {
  return routeManager.getModuleName(location);
}



// WEBPACK FOOTER //
// ./src/common/route-definitions.ts